/**
 * Componente atómico para el icono del botón
 *
 * Maneja el renderizado del icono principal del botón,
 * soportando FontAwesome icons y ReactElements.
 */

import React from 'react';
import { Icon } from '@/modules/Icon';
import {
  IconFontColor,
  IconFontSize,
  AnyIconDefinition,
  ButtonColors,
  VisualSize,
} from '@/shared/types/types';
import type { IconBackgroundColor } from '@/shared/types/icon';
import { cn } from '@/shared/utils/cn';
import { isFontAwesomeIcon } from '../utils/iconUtils';
import { getIconFontColor } from '../utils/colorUtils';

export interface ButtonIconProps {
  /** Icono a renderizar */
  icon: AnyIconDefinition | React.ReactElement;
  /** Tamaño del botón */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  /** Color del botón */
  color?: ButtonColors;
  /** Color personalizado del icono */
  colorIcon?: string;
  /** Clases CSS adicionales para el icono */
  classNameIcon?: string;
  /** Ancho de línea del icono */
  strokeWidth?: number;
  /** Tema del botón (`icon`: el clic se delega al glifo). @deprecated Preferir API sin `theme`; ver documentación del botón. */
  theme?: 'solid' | 'icon';
  /** Handler de click del icono */
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Handler de click del icono (alternativo) */
  onClickIcon?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Props de duotone para el icono */
  iconDuotonePrimary?: IconFontColor;
  iconDuotoneSecondary?: IconFontColor;
  iconDuotoneOpacityPrimary?: number;
  iconDuotoneOpacitySecondary?: number;
  /** Tamaño del glifo (si no se pasa, se deriva del tamaño del botón). */
  iconFontSize?: IconFontSize;
  background?: boolean;
  backgroundColor?: IconBackgroundColor;
  backgroundSize?: VisualSize;
  padding?: 'none' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

/**
 * Renderiza el icono del botón
 *
 * @example
 * ```tsx
 * <ButtonIcon
 *   icon="ImageOutlined"
 *   size="md"
 *   color="primary"
 *   theme="solid"
 * />
 * ```
 */
export const ButtonIcon: React.FC<ButtonIconProps> = ({
  icon,
  size,
  color,
  colorIcon,
  classNameIcon,
  strokeWidth = 2,
  theme,
  onClick,
  onClickIcon,
  iconDuotonePrimary,
  iconDuotoneSecondary,
  iconDuotoneOpacityPrimary,
  iconDuotoneOpacitySecondary,
  iconFontSize,
  background = false,
  backgroundColor,
  backgroundSize,
  padding,
}) => {
  const resolvedIconSize = iconFontSize ?? (size === 'xxs' ? 'xs' : 'sm');

  // Si es un FontAwesome icon, renderizar con Icon
  if (isFontAwesomeIcon(icon)) {
    return (
      <Icon
        className={classNameIcon}
        name={icon}
        size={resolvedIconSize}
        color={
          getIconFontColor(
            color,
            colorIcon as IconFontColor | IconFontColor | undefined,
          ) as IconFontColor
        }
        onClick={theme === 'icon' ? onClick || onClickIcon : undefined}
        colorDuotonePrimary={iconDuotonePrimary}
        colorDuotoneSecondary={iconDuotoneSecondary}
        opacityDuotonePrimary={iconDuotoneOpacityPrimary}
        opacityDuotoneSecondary={iconDuotoneOpacitySecondary}
        background={background}
        backgroundColor={backgroundColor}
        backgroundSize={backgroundSize}
        padding={padding}
      />
    );
  }

  // Si es un AnyIconDefinition (objeto FontAwesome), renderizar con Icon
  if (typeof icon === 'object' && icon !== null && 'icon' in icon) {
    return (
      <Icon
        className={classNameIcon}
        name={icon as AnyIconDefinition}
        size={resolvedIconSize}
        color={
          getIconFontColor(
            color,
            colorIcon as IconFontColor | IconFontColor | undefined,
          ) as IconFontColor
        }
        onClick={theme === 'icon' ? onClick || onClickIcon : undefined}
        colorDuotonePrimary={iconDuotonePrimary}
        colorDuotoneSecondary={iconDuotoneSecondary}
        opacityDuotonePrimary={iconDuotoneOpacityPrimary}
        opacityDuotoneSecondary={iconDuotoneOpacitySecondary}
        background={background}
        backgroundColor={backgroundColor}
        backgroundSize={backgroundSize}
        padding={padding}
      />
    );
  }

  // Si es un ReactElement, renderizarlo directamente
  if (React.isValidElement(icon)) {
    return <>{icon}</>;
  }

  // Si no es ninguno de los tipos soportados, no renderizar nada
  return null;
};

ButtonIcon.displayName = 'ButtonIcon';
